import {
  ANCHOR,
  ANCHOR_STATES,
  HELMSMAN,
  HELMSMAN_INCIDENT_DATE_CUTOFF,
  HELMSMAN_STATES,
  SEDGWICK,
  SEDGWICK_CANADA,
  SEDGWICK_STATES,
  UNSUPPORTED_TPA,
} from "src/forms/constants";
import { getWcCountryConfig, isNonUsCountryConfig } from "src/app/wcCountryConfig";
import { areFeatureFlagsLoaded } from "src/app/featureFlags";

import type { WcPreScreenData } from "./types";

/** Inputs accepted by `resolveWcTpa`. All keys are optional so callers
 *  can pass partially-populated state from the pre-screen. */
export interface WcTpaResolutionInput {
  /** ISO country code or legacy "CAN" form. */
  country?: string;
  /** US state code (or Canadian province). Ignored when `country` is non-US. */
  state?: string;
  /** Incident date in any `Date.parse`-compatible string. */
  incidentDate?: string;
}

/**
 * Resolves the Workers Compensation TPA from incident site + date.
 *
 * Returns one of the legacy lowercase TPA identifiers (`sedgwick`,
 * `anchor`, `helmsman`, `sedgwickCanada`) or `UNSUPPORTED_TPA` when
 * inputs are sufficient but no TPA matches. Returns `null` when inputs
 * are insufficient — the caller should keep the pre-screen submit
 * disabled until the resolver can produce a definitive answer.
 *
 * Mirrors the legacy `getTpaFromSiteAndDate` previously inlined in
 * `WorkersCompensationApp.tsx`. Both call sites now share this helper
 * so the legacy WC wizard and the pre-screen always agree on the TPA.
 */
export function resolveWcTpa(input: WcTpaResolutionInput): string | null {
  const { country, state, incidentDate } = input;

  if (country) {
    const countryConfig = getWcCountryConfig(country);
    if (isNonUsCountryConfig(countryConfig)) {
      // Country is in WC_COUNTRY_CONFIGS (e.g. Canada). The country's
      // flow is gated by a feature flag, but if the flag cache is empty
      // (initial preload in flight or failed) `isEnabled()` would
      // silently return false and we would mis-classify a valid
      // non-US site as UNSUPPORTED_TPA. Defer the decision instead;
      // the caller re-runs once flags arrive.
      if (!areFeatureFlagsLoaded()) {
        return null;
      }
      return countryConfig.isEnabled() ? countryConfig.tpaIdentifier : UNSUPPORTED_TPA;
    }
  }

  if (!state || !incidentDate) {
    return null;
  }

  const date = new Date(incidentDate);
  if (
    SEDGWICK_STATES.includes(state) ||
    (date.getTime() < HELMSMAN_INCIDENT_DATE_CUTOFF.getTime() && HELMSMAN_STATES.includes(state))
  ) {
    return SEDGWICK;
  }
  if (ANCHOR_STATES.includes(state)) {
    return ANCHOR;
  }
  if (
    date.getTime() >= HELMSMAN_INCIDENT_DATE_CUTOFF.getTime() &&
    HELMSMAN_STATES.includes(state)
  ) {
    return HELMSMAN;
  }
  return UNSUPPORTED_TPA;
}

/** Schema-name segment form of the TPA used by the pre-screen state and
 *  the JSON-forms schema name (`WC-<segment>-v1_0_0`). */
export type WcSchemaTpa = WcPreScreenData["resolvedTpa"];

const TPA_TO_SCHEMA_SEGMENT: Record<string, WcSchemaTpa> = {
  [SEDGWICK]: "Sedgwick_US",
  [SEDGWICK_CANADA]: "Sedgwick_Canada",
  [ANCHOR]: "Anchor",
  [HELMSMAN]: "Helmsman",
};

/**
 * Maps a `resolveWcTpa` output to the schema-segment form stored in
 * `WcPreScreenData.resolvedTpa`. Returns `null` for `UNSUPPORTED_TPA`,
 * for resolver outputs the pre-screen does not know how to route
 * (defensive), and for the resolver's own `null` (not-enough-info)
 * sentinel — callers treat all three as "don't submit".
 */
export function toWcSchemaTpa(tpa: string | null): WcSchemaTpa | null {
  if (!tpa || tpa === UNSUPPORTED_TPA) {
    return null;
  }
  return TPA_TO_SCHEMA_SEGMENT[tpa] ?? null;
}
